import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, BadgeCheck, Gift, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Support TravelerPulse Initiatives — voluntary contributions (owner plan §13-16).
   Presets $5-$100 + custom; $25+ grants a complimentary year of Individual membership. */

const PRESETS = [500, 1000, 2500, 5000, 10000];
const TIERS = [
  { min: 2500, label: "Supporter" },
  { min: 5000, label: "Explorer" },
  { min: 10000, label: "Navigator" },
  { min: 25000, label: "Global Traveler" },
];

export default function SupportUs() {
  const { user } = useAuth();
  const [sel, setSel] = useState(2500);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [disclaimer, setDisclaimer] = useState("Support payments are voluntary and are not charitable or tax-deductible donations unless specifically stated otherwise.");

  useEffect(() => {
    api.get("/memberships/config").then((r) => setDisclaimer(r.data.support_disclaimer)).catch(() => {});
  }, []);

  const amountCents = custom ? Math.round(parseFloat(custom || "0") * 100) : sel;

  const go = async () => {
    setBusy(true);
    try {
      const body = custom
        ? { custom_amount: parseFloat(custom), origin_url: window.location.origin }
        : { amount_key: String(sel / 100), origin_url: window.location.origin };
      const r = await api.post("/support/checkout", body);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="support-page">
      <section className="bg-rose-900 text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-rose-300 mb-4 flex items-center gap-2"><Heart className="w-4 h-4 fill-rose-300" /> Support Our Initiatives</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl leading-tight">Support TravelerPulse</h1>
          <p className="text-rose-100 text-lg mt-4 max-w-2xl">
            TravelerPulse.pro is being built to make travel information easier to discover, organize and share.
            We want to keep our global travel information available to travelers while continuing to develop
            new tools, databases and AI-powered travel services.
          </p>
          <p className="text-rose-200 mt-3 max-w-2xl">If TravelerPulse has helped you, please consider supporting our initiatives.
            Every contribution helps us build a better TravelerPulse for travelers everywhere.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <h2 className="font-heading font-900 text-2xl mb-4">Choose an amount</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((c) => (
              <button key={c} onClick={() => { setSel(c); setCustom(""); }}
                data-testid={`support-amount-${c / 100}`}
                className={`px-5 py-3 font-heading font-900 text-lg border-2 transition-colors ${!custom && sel === c ? "border-rose-600 bg-rose-50 text-rose-700" : "border-zinc-300 bg-white hover:border-rose-400"}`}>
                ${c / 100}
              </button>
            ))}
            <div className={`flex items-center border-2 px-3 ${custom ? "border-rose-600 bg-rose-50" : "border-zinc-300 bg-white"}`}>
              <span className="text-lg font-700 text-zinc-500">$</span>
              <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="Other" data-testid="support-custom-input"
                className="w-24 px-2 py-3 bg-transparent outline-none font-heading font-900 text-lg" />
            </div>
          </div>

          {amountCents >= 2500 && (
            <div className="border border-emerald-300 bg-emerald-50 p-4 mb-4 text-sm" data-testid="support-bonus-note">
              <div className="font-700 text-emerald-800 flex items-center gap-1.5"><Gift className="w-4 h-4" /> Includes a thank-you gift</div>
              <p className="text-emerald-900 mt-1">Support with $25 or more and receive <b>one complimentary year of My Travel Hub Individual membership</b>. This is a promotional benefit and does not auto-renew.{!user && " Sign in before contributing so we can attach it to your account."}</p>
            </div>
          )}

          <button onClick={go} disabled={busy || !amountCents || amountCents < 100} data-testid="support-checkout-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-rose-600 text-white font-heading font-900 text-lg px-10 py-4 hover:bg-rose-500 transition-colors disabled:opacity-50">
            <Heart className="w-5 h-5 fill-white" /> {busy ? "Redirecting…" : `Support with $${(amountCents / 100).toFixed(2)}`}
          </button>

          <p className="text-xs text-muted-foreground mt-5" data-testid="support-disclaimer">{disclaimer}</p>
        </div>

        <div className="lg:col-span-2 space-y-5">
          <div className="border border-zinc-300 bg-white p-5">
            <h3 className="font-heading font-900 text-lg mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Founding Supporter badges</h3>
            <p className="text-sm text-zinc-600 mb-3">Early supporters receive a permanent profile badge:</p>
            <div className="space-y-2">
              {TIERS.map((t) => (
                <div key={t.label} className="flex items-center justify-between text-sm border border-zinc-200 px-3 py-2" data-testid={`tier-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="inline-flex items-center gap-1.5 font-700"><BadgeCheck className="w-4 h-4 text-amber-600" /> {t.label}</span>
                  <span className="text-muted-foreground">${t.min / 100}+</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-indigo-300 bg-indigo-50/60 p-5 text-sm">
            <div className="font-700 text-indigo-900 mb-1">Looking for the membership instead?</div>
            <p className="text-indigo-900/80 mb-2">My Travel Hub gives you trips, documents, an AI assistant and more — free for 45 days.</p>
            <Link to="/membership" data-testid="support-to-membership" className="text-indigo-700 font-700 hover:underline">See membership plans →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
